import { BellOff, CheckCheck } from "lucide-react";

import { NotificationItem } from "@/features/notifications/components/NotificationItem";
import { useNotifications } from "@/features/notifications/api";
import type { Notification } from "@/features/notifications/lib/sentences";
import { useT } from "@/shared/i18n/useT";
import { Button } from "@/shared/ui/Button";
import { EmptyState } from "@/shared/ui/EmptyState";
import { InlineAlert } from "@/shared/ui/InlineAlert";
import { Skeleton } from "@/shared/ui/Skeleton";

function ListSkeleton() {
  return (
    <ul aria-hidden="true" className="space-y-4 px-4 py-4">
      {Array.from({ length: 4 }, (_, index) => (
        <li key={index} className="flex gap-3">
          <Skeleton className="size-7 rounded-full" />
          <span className="flex-1 space-y-1.5">
            <Skeleton className="h-3.5 w-4/5" />
            <Skeleton className="h-3 w-1/3" />
          </span>
        </li>
      ))}
    </ul>
  );
}

interface NotificationListProps {
  unreadOnly: boolean;
  onOpen: (notification: Notification) => void;
}

/** The notifications of one tab, newest first, with "Load more" at the end. */
export function NotificationList({ unreadOnly, onOpen }: NotificationListProps) {
  const t = useT();
  const query = useNotifications(unreadOnly);
  const items = query.data?.pages.flatMap((page) => page.notifications) ?? [];

  if (query.isError && items.length === 0) {
    return (
      <div className="p-4">
        <InlineAlert
          tone="danger"
          title={t("notifications.loadError")}
          action={
            <Button variant="secondary" size="sm" onClick={() => void query.refetch()}>
              {t("common.retry")}
            </Button>
          }
        />
      </div>
    );
  }
  if (query.isPending) return <ListSkeleton />;
  if (items.length === 0) {
    return unreadOnly ? (
      <EmptyState
        size="sm"
        className="py-12"
        icon={CheckCheck}
        title={t("notifications.emptyUnread.title")}
        body={t("notifications.emptyUnread.body")}
      />
    ) : (
      <EmptyState
        size="sm"
        className="py-12"
        icon={BellOff}
        title={t("notifications.empty.title")}
        body={t("notifications.empty.body")}
      />
    );
  }

  return (
    <>
      <ul className="divide-y divide-line">
        {items.map((notification) => (
          <NotificationItem key={notification.id} notification={notification} onOpen={onOpen} />
        ))}
      </ul>
      {query.hasNextPage ? (
        <div className="border-t border-line p-2">
          <Button
            variant="ghost"
            size="sm"
            className="w-full"
            disabled={query.isFetchingNextPage}
            aria-busy={query.isFetchingNextPage || undefined}
            onClick={() => void query.fetchNextPage()}
          >
            {query.isFetchingNextPage
              ? t("notifications.loadingMore")
              : t("notifications.loadMore")}
          </Button>
        </div>
      ) : null}
    </>
  );
}
